import { useState, useEffect, useCallback } from 'react'
import { M365SignIn, M365UserSignInsResponse } from '../../types/m365Users'
import { useAuth } from '../../hooks/useAuth'
import { api } from '../../services/api'
import { SignInDetailPanel } from './SignInDetailPanel'

interface ActivitySectionProps {
  userId: string
  customerId: string
  customerTenantId: string
}

export function ActivitySection({
  userId,
  customerId,
  customerTenantId,
}: ActivitySectionProps) {
  const { getCustomerGraphAccessToken } = useAuth()
  const [signIns, setSignIns] = useState<M365SignIn[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedSignIn, setSelectedSignIn] = useState<M365SignIn | null>(null)
  const [showRiskyOnly, setShowRiskyOnly] = useState(false)

  const fetchSignIns = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      const token = await getCustomerGraphAccessToken(customerTenantId)
      const response = await api.getM365UserSignIns(token, customerId, userId, { top: 50 }) as M365UserSignInsResponse

      setSignIns(response.signIns || [])
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load sign-in activity'
      if (errorMessage.includes('Authorization_RequestDenied') || errorMessage.includes('AuditLog')) {
        setError('Insufficient permissions to view sign-in activity. AuditLog.Read.All permission is required.')
      } else {
        setError(errorMessage)
      }
    } finally {
      setLoading(false)
    }
  }, [userId, customerId, customerTenantId, getCustomerGraphAccessToken])

  useEffect(() => {
    fetchSignIns()
  }, [fetchSignIns])

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const getStatusBadge = (status: M365SignIn['status']) => {
    if (status.errorCode === 0) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          <span className="h-2 w-2 rounded-full bg-green-400 mr-1.5"></span>
          Success
        </span>
      )
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800" title={status.failureReason}>
        <span className="h-2 w-2 rounded-full bg-red-400 mr-1.5"></span>
        Failed
      </span>
    )
  }

  const getLocationString = (location?: M365SignIn['location']) => {
    if (!location) return '-'
    const parts = [location.city, location.state, location.countryOrRegion].filter(Boolean)
    return parts.length > 0 ? parts.join(', ') : '-'
  }

  const getRiskBadge = (signIn: M365SignIn) => {
    if (!signIn.riskLevelDuringSignIn || signIn.riskLevelDuringSignIn === 'none') {
      return null
    }

    const riskConfig: Record<string, { color: string; label: string; tooltip: string }> = {
      low: {
        color: 'bg-yellow-100 text-yellow-800',
        label: 'Low Risk',
        tooltip: 'Minor risk indicators detected during sign-in',
      },
      medium: {
        color: 'bg-orange-100 text-orange-800',
        label: 'Medium Risk',
        tooltip: 'Moderate risk indicators detected - review recommended',
      },
      high: {
        color: 'bg-red-100 text-red-800',
        label: 'High Risk',
        tooltip: 'Significant risk detected - immediate review recommended',
      },
      hidden: {
        color: 'bg-gray-100 text-gray-800',
        label: 'Risk Hidden',
        tooltip: 'Risk level not available (requires Azure AD Premium P2)',
      },
      unknownFutureValue: {
        color: 'bg-gray-100 text-gray-800',
        label: 'Unknown',
        tooltip: 'Risk level could not be determined',
      },
    }

    const config = riskConfig[signIn.riskLevelDuringSignIn] || {
      color: 'bg-gray-100 text-gray-800',
      label: signIn.riskLevelDuringSignIn,
      tooltip: 'Risk level reported by Azure AD',
    }

    return (
      <span
        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${config.color} cursor-help`}
        title={config.tooltip}
      >
        {config.label}
      </span>
    )
  }

  // Filter for risky sign-ins
  const riskySignIns = signIns.filter(
    s => s.riskLevelDuringSignIn && s.riskLevelDuringSignIn !== 'none'
  )
  const displayedSignIns = showRiskyOnly ? riskySignIns : signIns

  if (loading) {
    return (
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-center py-8">
            <svg className="animate-spin h-6 w-6 text-primary-600" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
            </svg>
            <span className="ml-2 text-sm text-gray-500">Loading sign-in activity...</span>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="bg-white shadow sm:rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <div className="flex">
              <svg className="h-5 w-5 text-yellow-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              <div>
                <h3 className="text-sm font-medium text-yellow-800">Unable to load sign-in activity</h3>
                <p className="mt-1 text-sm text-yellow-700">{error}</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="bg-white shadow sm:rounded-lg">
      <div className="px-4 py-5 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            Sign-in Activity ({showRiskyOnly ? `${displayedSignIns.length} risky` : `Last ${signIns.length}`} sign-ins)
          </h3>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowRiskyOnly(!showRiskyOnly)}
              className={`inline-flex items-center px-3 py-1.5 border shadow-sm text-sm font-medium rounded-md ${
                showRiskyOnly
                  ? 'border-red-300 text-red-700 bg-red-50 hover:bg-red-100'
                  : 'border-gray-300 text-gray-700 bg-white hover:bg-gray-50'
              }`}
              title={showRiskyOnly ? 'Show all sign-ins' : 'Show only risky sign-ins'}
            >
              <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              {showRiskyOnly ? 'Showing Risky' : 'Risky'}
              {riskySignIns.length > 0 && (
                <span className={`ml-1.5 inline-flex items-center justify-center px-2 py-0.5 rounded-full text-xs font-medium ${
                  showRiskyOnly ? 'bg-red-200 text-red-800' : 'bg-red-100 text-red-700'
                }`}>
                  {riskySignIns.length}
                </span>
              )}
            </button>
            <button
              onClick={fetchSignIns}
              className="inline-flex items-center px-3 py-1.5 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
            >
              <svg className="h-4 w-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
              </svg>
              Refresh
            </button>
          </div>
        </div>

        {signIns.length === 0 ? (
          <p className="text-sm text-gray-500 italic">No sign-in activity found</p>
        ) : displayedSignIns.length === 0 ? (
          <div className="text-center py-8">
            <svg className="mx-auto h-12 w-12 text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <h3 className="mt-2 text-sm font-medium text-gray-900">No risky sign-ins</h3>
            <p className="mt-1 text-sm text-gray-500">All {signIns.length} sign-ins have no detected risk.</p>
            <button
              onClick={() => setShowRiskyOnly(false)}
              className="mt-3 text-sm text-primary-600 hover:text-primary-700"
            >
              Show all sign-ins
            </button>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date/Time
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Application
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Location
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Device/Browser
                  </th>
                  <th scope="col" className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    IP Address
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {displayedSignIns.map(signIn => (
                  <tr
                    key={signIn.id}
                    className="hover:bg-gray-50 cursor-pointer"
                    onClick={() => setSelectedSignIn(signIn)}
                  >
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">{formatDateTime(signIn.createdDateTime)}</div>
                      <div className="text-xs text-gray-500">
                        {signIn.isInteractive ? 'Interactive' : 'Non-interactive'}
                      </div>
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm font-medium text-gray-900">{signIn.appDisplayName || 'Unknown'}</div>
                      {signIn.resourceDisplayName && (
                        <div className="text-xs text-gray-500">Resource: {signIn.resourceDisplayName}</div>
                      )}
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap">
                      <div className="flex flex-col gap-1">
                        {getStatusBadge(signIn.status)}
                        {getRiskBadge(signIn)}
                        {signIn.status.failureReason && (
                          <div className="text-xs text-red-600 max-w-xs truncate" title={signIn.status.failureReason}>
                            {signIn.status.failureReason}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                      {getLocationString(signIn.location)}
                    </td>
                    <td className="px-4 py-4">
                      <div className="text-sm text-gray-900">{signIn.deviceDetail?.browser || '-'}</div>
                      <div className="text-xs text-gray-500">{signIn.deviceDetail?.operatingSystem || ''}</div>
                    </td>
                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500 font-mono">
                      {signIn.ipAddress || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Sign-in detail panel */}
      {selectedSignIn && (
        <SignInDetailPanel
          signIn={selectedSignIn}
          onClose={() => setSelectedSignIn(null)}
        />
      )}
    </div>
  )
}
